import React from "react";
import { prefersReducedMotion } from "../../../styles/particleField";

/**
 * CardGridSkeleton — placeholder tiles in the exact geometry of the real grid.
 *
 * A centred spinner on a blank page tells the user "something is happening
 * somewhere else", then reflows the whole screen when content lands. Tiles that
 * match the real layout tell them "your cards are arriving, here" and the
 * content swaps in without the page jumping.
 *
 * Mirrors Card.jsx's structure deliberately: 3/4 art box, title line, then a
 * price/actions row. If Card's layout changes, this should change with it.
 */
const CardGridSkeleton = ({ count = 6, columns = 2, gap = 14 }) => {
  const reduced = prefersReducedMotion();
  const shimmer = reduced
    ? undefined
    : "cardSkeletonShimmer 1.4s ease-in-out infinite";

  return (
    <div
      data-testid="card-grid-skeleton"
      data-skeleton-count={count}
      aria-hidden="true"
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${columns}, minmax(0, 1fr))`,
        gap,
      }}
    >
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          style={{
            background: "#ffffff",
            borderRadius: 16,
            overflow: "hidden",
            border: "1.5px solid rgba(107,33,168,0.08)",
            // Staggered so the block reads as filling in, not as one flat slab.
            animation: reduced ? undefined : `cardSkeletonIn 320ms ease-out ${i * 45}ms both`,
          }}
        >
          <div
            style={{
              aspectRatio: "3/4",
              background:
                "linear-gradient(100deg, #eef2f7 30%, #f8fafc 50%, #eef2f7 70%)",
              backgroundSize: "220% 100%",
              animation: shimmer,
            }}
          />
          <div style={{ padding: "10px 12px 12px" }}>
            <div
              style={{
                height: 11,
                width: "72%",
                borderRadius: 4,
                background:
                  "linear-gradient(100deg, #eef2f7 30%, #f8fafc 50%, #eef2f7 70%)",
                backgroundSize: "220% 100%",
                animation: shimmer,
              }}
            />
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                marginTop: 12,
                gap: 6,
              }}
            >
              <div
                style={{
                  height: 16,
                  width: 54,
                  borderRadius: 4,
                  background:
                    "linear-gradient(100deg, #eef2f7 30%, #f8fafc 50%, #eef2f7 70%)",
                  backgroundSize: "220% 100%",
                  animation: shimmer,
                }}
              />
              <div style={{ display: "flex", gap: 6 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#eef2f7" }} />
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#eef2f7" }} />
              </div>
            </div>
          </div>
        </div>
      ))}

      <style>{`
        @keyframes cardSkeletonShimmer {
          from { background-position: 180% 0; }
          to   { background-position: -80% 0; }
        }
        @keyframes cardSkeletonIn {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CardGridSkeleton;
